import { useAppDispatch, useAppSelector } from "../../../state/redux/hooks";
import {
  selectViewingRecord,
  setViewingRecord,
} from "../../../state/redux/slices/ui";
import { Button } from "../button";
import {
  SquareArrowOutUpRight,
  X,
  FileText,
  FileCode,
  Edit,
} from "lucide-react";
import { ExternalLink } from "lucide-react";
import RecordView from "./RecordView";
import RecordSheet from "./RecordSheet";
import { useState } from "react";
import RecordIntervalName from "./RecordIntervalName";
import { useCanEditRecord, useFullRecord } from "@/api/hooks";

export function ExternalUrl({ href, label }: { href: string; label?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
    >
      <span>{label ?? href}</span>
      <ExternalLink className="h-4 w-4" aria-hidden="true" />
    </a>
  );
}

export default function RecordPreview() {
  const dispatch = useAppDispatch();
  const listRecord = useAppSelector(selectViewingRecord);
  const record = useFullRecord(listRecord);
  // The excerpt is fine to *read* while the full text is on its way, but not to
  // edit: the form seeds from this record, so saving from it would write the
  // excerpt back over the record. `content_truncated` clears when the fetch
  // lands, and the button appears then.
  const canEdit = useCanEditRecord(record) && !record?.content_truncated;
  const [markdownEnabled, setMarkdownEnabled] = useState(true);

  function hasMarkdown(content: string | null | undefined) {
    if (!content) return false;
    const mdRegex =
      /(^|\n)(#{1,6}\s)|(`{1,3})|(\*\*|__)|(^|\n)([-*+]\s|\d+\.)|(\[.+?\]\(.+?\))|(^|\n)>/m;
    return mdRegex.test(content);
  }
  // An email body is plain text that happens to contain asterisks and
  // leading ">" quote markers, which markdown would silently turn into
  // emphasis and blockquotes. RecordView's plain branch renders it
  // whitespace-pre-line, which is what a mail body wants anyway.
  const isExternal = !!record?.external;
  const contentHasMarkdown = !isExternal && hasMarkdown(record?.content);

  const handleClose = () => dispatch(setViewingRecord(null));

  if (!record) return null;

  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Fixed header: title + date + rule */}
      <div className="px-[38px] pt-[30px] pb-0 flex-shrink-0">
        <div className="flex justify-between items-start gap-4 pr-8">
          <h1
            className="text-[25px] font-semibold leading-[1.18] text-foreground"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            {record.title || "No title"}
          </h1>
        </div>
        {"interval" in record && (
          <div className="font-mono text-[12px] text-muted-foreground mt-[11px] tracking-[.01em]">
            <RecordIntervalName record={record} />
          </div>
        )}
        <div className="w-[40px] h-[2px] bg-primary mt-[22px] mb-[22px]" />
      </div>
      {/* Scrollable body: only RecordView content scrolls */}
      <div className="grow overflow-auto px-[38px] pb-6">
        <RecordView
          record={record}
          markdownEnabled={markdownEnabled && !isExternal}
        />
      </div>
      {/* Footer toolbar */}
      <div className="flex-shrink-0 border-t border-border px-[26px] py-[13px] flex items-center justify-between">
        <div>
          {contentHasMarkdown && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMarkdownEnabled(!markdownEnabled)}
              className="gap-1 text-muted-foreground hover:text-foreground"
            >
              {markdownEnabled ? (
                <FileText className="size-4" />
              ) : (
                <FileCode className="size-4" />
              )}
              {markdownEnabled ? "Raw" : "Markdown"}
            </Button>
          )}
        </div>
        <div className="flex justify-end gap-1">
          {/* An anchor, not a navigate(): the preview sits beside the list it
              belongs to, and following it in place would close the list to show
              what is already on screen. A real href also means middle-click and
              the context menu do what they look like they do. */}
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="w-[30px] h-[30px] rounded-[6px] text-muted-foreground hover:bg-row-hover hover:text-foreground"
          >
            <a
              // An external record has no permalink here -- there is no row to
              // serve one from -- so the link goes where the thing actually
              // lives.
              href={record.external?.href ?? `/record/${record.id}`}
              target="_blank"
              rel="noopener noreferrer"
              title={
                record.external
                  ? "Open in the source system"
                  : "Open record in a new tab"
              }
              aria-label={
                record.external
                  ? "Open in the source system"
                  : "Open record in a new tab"
              }
            >
              <SquareArrowOutUpRight className="size-[15px]" />
            </a>
          </Button>
          {canEdit && (
            <RecordSheet record={record} title="Edit record">
              <Button
                variant="ghost"
                size="icon"
                className="w-[30px] h-[30px] rounded-[6px] text-muted-foreground hover:bg-row-hover hover:text-foreground"
              >
                <Edit className="size-[15px]" />
              </Button>
            </RecordSheet>
          )}
        </div>
      </div>
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClose}
        className="absolute top-[28px] right-[26px] w-[28px] h-[28px] rounded-[6px] text-muted-foreground hover:bg-row-hover hover:text-foreground"
      >
        <X className="size-[14px]" />
      </Button>
    </div>
  );
}
